import React, { useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import _get from 'lodash/get';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import {
  DataSourceType,
  Category,
  DashboardInjectedProps,
  TableModuleColumnConfig,
  Tag,
  FilterCodeConfig,
} from '../../util/types';
import getFhirSearchConfig from './util/getFhirSearchConfig';
import parseResponses, {
  ParseResult,
  emptyParseResult,
} from './util/parseResponses';
import getConfiguredColumnSet from './util/getConfiguredColumnSet';
import { ConfiguredMomentFormat } from '../../util/momentFormats';
import {
  TableConfiguration,
  TableModule,
  TableModuleProps as ChromaTableModuleProps,
  TableSortState,
} from '@lifeomic/chroma-react/components/TableModule';
import getDefaultColumnSort from './util/getDefaultColumnSort';
import ModuleHeader from './ModuleHeader';
import { IsInEditModeContext } from '../../../LayoutConfiguration/util/DashboardsContextRoot';
import usePatientViewerFhirSearch, {
  TimeFilterConfig,
} from '../../hooks/usePatientViewerFhirSearch';
import { useCustomCompareEffect } from 'react-use';
import _isEqual from 'lodash/isEqual';
import { useUpdateTimelineOnFavorite } from '../../common/Favorite/useUpdateTimelineOnFavorite';

export const useStyles = makeStyles((theme: Theme) => ({
  bkgd: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : theme.palette.black[50],
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
  },
  tableRootContainer: {
    flex: 1,
    overflow: 'hidden',
    padding: theme.spacing(0, 1, 1),
    zIndex: 0,
  },
  tableContainer: {
    height: '100%',
    overflow: 'auto',
  },
  table: {
    /*
     * Chroma row action cells draw an extra pseudo-element divider in addition
     * to the row/cell border. In this wrapper that shows up as a doubled
     * bottom border on the trailing action column.
     */
    '& tbody tr td:last-child::after': {
      display: 'none',
    },
  },
  loadingTableClass: {
    display: 'flex',
    justifyContent: 'center',
  },
  cell: {
    whiteSpace: 'unset',
  },
}));

export type TableModuleClasses = GetClasses<typeof useStyles>;

export type TableModuleOwnProps = {
  dataSource: DataSourceType;
  useTagFilter?: boolean;
  categories?: Category[];
  useCategoryFilter?: boolean;
  timeFilterProps: TimeFilterConfig;
  momentFormat: ConfiguredMomentFormat;
  columns?: TableModuleColumnConfig[];
  filters?: Tag[];
  useNotTagFilter?: boolean;
  notTagFilters?: Tag[];
  pageSize?: number;
  applyDateFilter?: boolean;
  /** Extra table columns prepended before configured FHIR columns (same row order). */
  prependColumns?: TableConfiguration[];
  /** Extra table columns appended after configured FHIR columns (same row order). */
  appendColumns?: TableConfiguration[];
  /** Optional actions rendered in the table module header. */
  headerActions?: React.ReactNode;
  /** Optional class applied to the underlying Chroma table. */
  tableClassName?: string;
  /** Optional callback with the rows currently rendered by the table. */
  onRowsResolved?: (rows: ParseResult['rows']) => void;
  /** Optional row actions rendered with the table's grouped action affordance. */
  rowActions?: ChromaTableModuleProps['rowActions'];
  /** Optional resource-level predicate for consumers with table-specific exclusions. */
  filterResources?: (resource: fhir.Resource) => boolean;
  /** Passed to the FHIR search hook to force a refetch when the value changes. */
  refetchDependencies?: unknown[];
} & DashboardInjectedProps &
  FilterCodeConfig;

export type TableModuleProps = TableModuleOwnProps;

const SubjectViewerTableModule = (props: TableModuleProps): any => {
  const {
    appendColumns,
    headerActions,
    onRowsResolved,
    prependColumns,
    rowActions,
    tableClassName,
  } = props;
  const { onFavoriteUpdate } = useUpdateTimelineOnFavorite();
  const formatMessage = useIntl().formatMessage;
  const classes = useStyles(props);
  const isInEditMode = useContext(IsInEditModeContext);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [searchValue, setSearchValue] = useState<string>(null);
  const [parseResult, setParseResult] = useState<ParseResult>(emptyParseResult);
  const [sortState, setSortState] = useState<TableSortState>(
    getDefaultColumnSort(
      getConfiguredColumnSet(
        props.dataSource,
        props.columns,
        props.momentFormat,
      ),
    ),
  );

  const searchConfig = getFhirSearchConfig(props);
  const { isLoading, searchResults, loadMore, hasMoreToLoad } =
    usePatientViewerFhirSearch({
      searchConfig: searchConfig,
      inMemoryPaginationPageSize: searchConfig.pageSize || 30,
      dependencies: props.refetchDependencies,
    });

  useCustomCompareEffect(
    () => {
      // reset sort if column config changes
      const configuredColumns = getConfiguredColumnSet(
        props.dataSource,
        props.columns,
        props.momentFormat,
      );
      const newState = getDefaultColumnSort(configuredColumns);
      if (
        newState.sortKey !== sortState.sortKey ||
        newState.sortDirection !== sortState.sortDirection
      ) {
        setSortState(newState);
      }
    },
    [
      props.dataSource,
      props.columns,
      props.momentFormat,
      sortState.sortKey,
      sortState.sortDirection,
    ],
    (
      [prevSource, prevColumns, prevMomentFormat],
      [source, columns, momentFormat],
    ) => {
      return (
        _isEqual(prevSource, source) &&
        _isEqual(prevColumns, columns) &&
        _isEqual(prevMomentFormat, momentFormat)
      );
    },
  );

  useEffect(() => {
    // this makes the assumption that the (datasource -> resource type) mapping will always be 1:1 for tables
    if (!searchResults || !searchResults[0]) {
      setParseResult(emptyParseResult);
      onRowsResolved?.(emptyParseResult.rows);
      return;
    }

    const results = _get(searchResults, [0, 0, 'results'], []);
    const filterResources = props.filterResources;
    const filteredResults = filterResources
      ? results.filter(({ resource }) => filterResources(resource))
      : results;
    const configuredColumns = getConfiguredColumnSet(
      props.dataSource,
      props.columns,
      props.momentFormat,
    );

    const parseResult = parseResponses(
      props.dataSource,
      configuredColumns,
      filteredResults,
      sortState,
      setSortState,
      props.momentFormat,
      formatMessage,
      searchValue,
      isInEditMode,
      classes.cell,
      onFavoriteUpdate,
    );

    if (prependColumns?.length || appendColumns?.length) {
      const nextParseResult = {
        config: [
          ...(prependColumns ?? []),
          ...parseResult.config,
          ...(appendColumns ?? []),
        ],
        rows: parseResult.rows,
      };
      setParseResult(nextParseResult);
      onRowsResolved?.(nextParseResult.rows);
    } else {
      setParseResult(parseResult);
      onRowsResolved?.(parseResult.rows);
    }
  }, [
    appendColumns,
    prependColumns,
    onRowsResolved,
    props.dataSource,
    props.filterResources,
    props.columns,
    props.momentFormat,
    isLoading,
    sortState.sortDirection,
    sortState.sortKey,
    searchValue,
    searchResults,
    sortState,
    formatMessage,
    isInEditMode,
    classes.cell,
    onFavoriteUpdate,
  ]);

  return (
    <div className={classes.bkgd}>
      <ModuleHeader
        isFetching={isLoading}
        searchResult={_get(searchResults, [0, 0])}
        hasMoreToLoad={hasMoreToLoad}
        loadMore={loadMore}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        actions={headerActions}
      />
      <div className={classes.tableRootContainer}>
        <div className={classes.tableContainer}>
          <TableModule
            className={[
              classes.table,
              tableClassName,
              isLoading && !parseResult.rows.length
                ? classes.loadingTableClass
                : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            isLoading={isLoading}
            config={parseResult.config}
            data={parseResult.rows}
            rowActions={
              isLoading && !parseResult.rows.length ? undefined : rowActions
            }
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(SubjectViewerTableModule);
